import { CareActionType } from './care.redux';
import { CareItem } from 'src/replicant/care/care.ruleset';
import { assertNever } from 'src/replicant/utils';
import {
  DiningRoomPosition,
  LivingRoomPosition,
  Position,
} from '../position/PositionModel';
import { AnimationState } from 'src/replicant/pet/PetAssets';
import StateObserver from 'src/StateObserver';
import features from 'src/replicant/ruleset/features';
import { getSceneManager } from 'src/lib/utils';

export const careActionAnimations: Record<CareActionType, AnimationState> = {
  toy: 'play',
  bath: 'bath',
  toilet: 'toilet',
  food: 'eat',
  snack: 'eat',
  firstAid: 'eat',
};

export const getCareActionType = ({
  type,
}: Exclude<CareItem, { type: 'invitation' }>): CareActionType =>
  type === 'first_aid' ? 'firstAid' : type;

export function getPetCarePosition(
  actionType: CareActionType,
  isVisitingFriend: boolean = false,
): Position {
  switch (actionType) {
    case 'toy':
    case 'firstAid':
      return {
        scene: getSceneManager().baseSceneId,
        location: !isVisitingFriend ? 'c' : 'lm',
      };

    case 'snack':
    case 'food':
      return {
        scene: 'diningRoom',
        location: !isVisitingFriend ? 'mat' : 'matPlayer',
      };

    case 'toilet':
      return {
        scene: 'toiletRoom',
        location: 'toilet',
      };

    case 'bath':
      return {
        scene: 'bathroom',
        location: 'bath',
      };

    default:
      assertNever(actionType);
  }
}

export function getFriendPetCarePosition(actionType: CareActionType): Position {
  switch (actionType) {
    case 'toy':
    case 'firstAid':
      return {
        scene: 'friendsLivingRoom',
        location: 'rm',
      };

    case 'snack':
    case 'food':
      return {
        scene: 'diningRoom',
        location: 'matFriend',
      };

    case 'toilet':
      return {
        scene: 'toiletRoom',
        location: 'side',
      };

    case 'bath':
      return {
        scene: 'bathroom',
        location: 'side',
      };

    default:
      assertNever(actionType);
  }
}

export function getCareItemPosition(
  item: CareItem,
  isVisitingFriend = false,
  isFriend = false,
): Position {
  const location = !isVisitingFriend
    ? 'floorLeft'
    : isFriend
      ? 'rm'
      : 'floorLeft';

  switch (item.type) {
    case 'first_aid':
      return {
        scene: getSceneManager().baseSceneId,
        location: location,
      };

    case 'snack':
    case 'food':
      return {
        scene: 'diningRoom',
        location: !isVisitingFriend
          ? 'plate'
          : isFriend
            ? 'plateFriend'
            : 'platePlayer',
      };

    case 'toy':
      return getToyPosition(item.item, isVisitingFriend);

    case 'invitation':
      // Not applicable.
      throw new Error('Cannot get position for invitation item. ');

    default:
      assertNever(item);
  }
}

function getToyPosition(
  item: Extract<CareItem, { type: 'toy' }>['item'],
  isVisitingFriend = false,
): Position {
  const scene = getSceneManager().baseSceneId;

  switch (item) {
    case 'autoPilotAirplane':
      return {
        scene,
        location: 'aboveLeft',
      };

    case 'commonBell':
      return {
        scene,
        location: isVisitingFriend ? 'frontCenter' : 'frontLeft',
      };

    case 'fairytaleUnicorn':
    case 'woollyCaterpillar':
      return {
        scene,
        location: isVisitingFriend ? 'floorRight' : 'floorFarRight',
      };

    case 'doremiPiano':
    case 'emptyMagicLamp':
    case 'noSurpriseJackInTheBox':
      return {
        scene,
        location: isVisitingFriend ? 'c' : 'floorLeft',
      };

    default:
      return {
        scene,
        location: 'c',
      };
  }
}
